// src/store/folderStore.ts

import type { IndexRow, VDRProviderId } from "../vdr/providers";

export type FolderRef = {
  driveId: string;   // Graph drive id (OneDrive or SharePoint library)
  itemId: string;    // Graph item id of the picked folder
  webUrl: string;    // Nice human URL (for display)
  name: string;      // Folder name
};

export type IndexMap = Record<string, string>; // code ("1.2.3") -> itemId

/* ========= FolderRef storage ========= */

const FOLDER_KEY = "__irlFolderRefs"; // localStorage key for picked folders

function readAllFolders(): Record<string, FolderRef> {
  try {
    const raw = localStorage.getItem(FOLDER_KEY);
    return raw ? (JSON.parse(raw) as Record<string, FolderRef>) : {};
  } catch {
    return {};
  }
}

function writeAllFolders(map: Record<string, FolderRef>) {
  try {
    localStorage.setItem(FOLDER_KEY, JSON.stringify(map));
  } catch {}
}

/** Save the upload destination for a given IRL/project id */
export function saveFolderRef(irlId: string, f: FolderRef) {
  const map = readAllFolders();
  map[irlId] = f;
  writeAllFolders(map);
}

/** Get the saved upload destination for a given IRL/project id */
export function getFolderRef(irlId: string): FolderRef | null {
  const map = readAllFolders();
  return map[irlId] ?? null;
}

/** Remove the saved destination */
export function clearFolderRef(irlId: string) {
  const map = readAllFolders();
  delete map[irlId];
  writeAllFolders(map);
}

/* ========= Index map storage (code -> itemId) ========= */

const INDEX_KEY = (irlId: string) => `__irlIndexMap_${irlId}`;

const ALL_PER_PROJECT_PREFIXES = [
  '__irlIndexMap_',
  '__irlIndexRows_',
  '__irlIndexRowsTs_',
  '__irlProvider_',
  '__irlSyncTs_',
  '__irlExcludedFolders_',
];

/**
 * Delete all per-project localStorage entries for project IDs no longer in the
 * active projects list. Call before saving a new index map to free up space.
 */
export function pruneStaleData(activeProjectIds: string[]): void {
  const activeSet = new Set(activeProjectIds);
  const toDelete: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    for (const prefix of ALL_PER_PROJECT_PREFIXES) {
      if (key.startsWith(prefix)) {
        const id = key.slice(prefix.length);
        if (!activeSet.has(id)) toDelete.push(key);
        break;
      }
    }
  }

  for (const key of toDelete) {
    try { localStorage.removeItem(key); } catch {}
  }
}

/** Persist the code->itemId mapping produced by the index build */
export function saveIndexMap(irlId: string, indexMap: IndexMap): void {
  try {
    localStorage.setItem(INDEX_KEY(irlId), JSON.stringify(indexMap));
  } catch {
    throw new Error('Storage quota exceeded — old project data has been pruned. Please try syncing again.');
  }
}

/** Retrieve the code->itemId mapping for a project (or null if missing) */
export function getIndexMap(irlId: string): IndexMap | null {
  try {
    const raw = localStorage.getItem(INDEX_KEY(irlId));
    return raw ? (JSON.parse(raw) as IndexMap) : null;
  } catch {
    return null;
  }
}

/** Remove the saved index mapping */
export function clearIndexMap(irlId: string) {
  try {
    localStorage.removeItem(INDEX_KEY(irlId));
  } catch {}
}

/* ========= Index rows storage (parsed index data) ========= */

const INDEX_ROWS_KEY = (irlId: string) => `__irlIndexRows_${irlId}`;

const INDEX_ROWS_TS_KEY = (irlId: string) => `__irlIndexRowsTs_${irlId}`;

/** Persist the parsed index rows (for Firmex file routing) */
export function saveIndexRows(irlId: string, rows: IndexRow[]) {
  try {
    localStorage.setItem(INDEX_ROWS_KEY(irlId), JSON.stringify(rows));
    localStorage.setItem(INDEX_ROWS_TS_KEY(irlId), new Date().toISOString());
  } catch {
    throw new Error('Storage quota exceeded — index rows could not be saved. File routing may not work until you re-sync.');
  }
}

/** Get the timestamp of the last saved index rows */
export function getIndexRowsTimestamp(irlId: string): string | null {
  try {
    return localStorage.getItem(INDEX_ROWS_TS_KEY(irlId));
  } catch {
    return null;
  }
}

/** Retrieve the parsed index rows for a project (or null if missing) */
export function getIndexRows(irlId: string): IndexRow[] | null {
  try {
    const raw = localStorage.getItem(INDEX_ROWS_KEY(irlId));
    return raw ? (JSON.parse(raw) as IndexRow[]) : null;
  } catch {
    return null;
  }
}

/** Remove the saved index rows */
export function clearIndexRows(irlId: string) {
  try {
    localStorage.removeItem(INDEX_ROWS_KEY(irlId));
  } catch {}
}

/* ========= Provider ID storage ========= */

const PROVIDER_KEY = (irlId: string) => `__irlProvider_${irlId}`;

/** Persist the VDR provider used for this project's index */
export function saveProviderId(irlId: string, providerId: VDRProviderId) {
  try {
    localStorage.setItem(PROVIDER_KEY(irlId), providerId);
  } catch {}
}

/** Retrieve the saved VDR provider for a project (or null if missing) */
export function getProviderId(irlId: string): VDRProviderId | null {
  try {
    return localStorage.getItem(PROVIDER_KEY(irlId)) as VDRProviderId | null;
  } catch {
    return null;
  }
}

/** Remove the saved provider */
export function clearProviderId(irlId: string) {
  try {
    localStorage.removeItem(PROVIDER_KEY(irlId));
  } catch {}
}

/* ========= SharePoint sync timestamp ========= */

const SYNC_TS_KEY = (irlId: string) => `__irlSyncTs_${irlId}`;

/** Save the timestamp of the last SharePoint tree sync */
export function saveSyncTimestamp(irlId: string, ts: string) {
  try {
    localStorage.setItem(SYNC_TS_KEY(irlId), ts);
  } catch {}
}

/** Get the timestamp of the last SharePoint tree sync */
export function getSyncTimestamp(irlId: string): string | null {
  try {
    return localStorage.getItem(SYNC_TS_KEY(irlId));
  } catch {
    return null;
  }
}

/** Remove the saved sync timestamp */
export function clearSyncTimestamp(irlId: string) {
  try {
    localStorage.removeItem(SYNC_TS_KEY(irlId));
  } catch {}
}

/* ========= Folder-mirroring exclusions ========= */

const EXCLUDED_FOLDERS_KEY = (irlId: string) => `__irlExcludedFolders_${irlId}`;

/** Persist the local folder paths (lowercase) excluded from folder mirroring */
export function saveExcludedFolders(irlId: string, paths: string[]) {
  try {
    if (paths.length === 0) localStorage.removeItem(EXCLUDED_FOLDERS_KEY(irlId));
    else localStorage.setItem(EXCLUDED_FOLDERS_KEY(irlId), JSON.stringify(paths));
  } catch {}
}

/** Retrieve the excluded folder paths for a project (empty if none saved) */
export function getExcludedFolders(irlId: string): string[] {
  try {
    const raw = localStorage.getItem(EXCLUDED_FOLDERS_KEY(irlId));
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
